import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideCta,
  GuideRelated,
  GuideInlineCta,
  CheckList,
  KeyStats,
  StepProcess,
  Callout,
  ProsCons,
  buildGuideHead,
  type GuideMeta,
} from "@/components/GuideLayout";

const meta: GuideMeta = {
  category: "Underhåll",
  title: "Skötsel & livslängd för fönsterfilm — så håller filmen 15+ år",
  tldr:
    "Modern 3M-fönsterfilm håller 12–15+ år vid invändig montering. För att garantin ska gälla behöver filmen rengöras med rätt teknik: mjuk mikrofiber, milt rengöringsmedel utan ammoniak, och aldrig slipmedel. 3M:s 10-årsgaranti via auktoriserad installatör täcker missfärgning, bubblor och separation — men inte slitskador eller felaktig rengöring. BBN Nordic lämnar 10 års produktgaranti + 10 års arbetsgaranti.",
  readTimeMin: 6,
  heroImage: IMAGES.installer,
  path: "/guider/underhall-livslangd",
  updated: "2026-06-16",
  faqs: [
    {
      q: "När får jag rengöra filmen efter installation?",
      a: "Vänta minst 30 dagar innan första våtrengöringen. Filmen härdar mot glaset under 2–4 veckor och små vattenbubblor som syns första veckan försvinner av sig själva.",
    },
    {
      q: "Vad får jag inte använda?",
      a: "Undvik ammoniak (vanligt i fönsterputs), slipmedel, stålull, vass skrapa och alkaliska rengöringsmedel. Dessa skadar filmens topcoat och kan ogiltigförklara garantin.",
    },
    {
      q: "Vad gör jag om filmen får repor eller skador?",
      a: "Lokal reparation går inte — den skadade rutan filmas om. Kostnaden är typiskt en bråkdel av nyinstallation eftersom mått och förlaga finns hos oss.",
    },
    {
      q: "Vad täcker 3M:s 10-årsgaranti?",
      a: "Garantin täcker missfärgning, gulning, blåsbildning, separation från glaset, sprickbildning i filmen och delaminering — under förutsättning att installationen är utförd av en auktoriserad installatör och att filmen är skött enligt 3M:s anvisningar.",
    },
    {
      q: "Vad gäller om jag säljer huset?",
      a: "Garantin är knuten till objektet, inte ägaren. Den nya ägaren tar över återstående garantitid och kan kontakta BBN Nordic för dokumentation.",
    },
  ],
  related: [
    { to: "/guider/solfilm-kontor", label: "Solfilm för kontor — guide" },
    { to: "/guider/solfilm-villa", label: "Solfilm för villa & lägenhet" },
    { to: "/datablad", label: "Datablad för 3M-filmer" },
  ],
  howTo: {
    name: "Så här rengör du fönsterfilm rätt",
    steps: [
      { name: "Vänta 30 dagar efter installation", text: "Filmen härdar mot glaset under 2–4 veckor. Undvik våtrengöring den första månaden — små vattenbubblor försvinner av sig själva." },
      { name: "Damma av torrt först", text: "Använd mjuk borste eller torr mikrofiberduk och ta bort partiklar innan du gör filmen våt, annars sliper partiklarna ner i topcoaten." },
      { name: "Blanda mild rengöringslösning", text: "Ljummet vatten med en droppe milt diskmedel utan citron, ammoniak eller slipmedel. Aldrig traditionell fönsterputs." },
      { name: "Tvätta med mikrofiber", text: "Ren mikrofiberduk eller mjuk svamp. Aldrig stålull, skurborstar, rakblad eller magic erasers — de river topcoaten." },
      { name: "Torka i en riktning", text: "Avsluta med torr mikrofiberduk i samma riktning. Minimerar strimmor och mekaniskt slitage på ytan." },
    ],
  },
};

export const Route = createFileRoute("/guider/underhall-livslangd")({
  head: () => buildGuideHead(meta),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />
      <GuideBody>
        <KeyStats
          items={[
            { value: "12–15 år", label: "Invändig film", sub: "förväntad livslängd" },
            { value: "7–10 år", label: "Utvändig film", sub: "exponerad för väder" },
            { value: "10 år", label: "Produktgaranti", sub: "3M via auktoriserad" },
            { value: "10 år", label: "Arbetsgaranti", sub: "BBN-installation" },
          ]}
        />

        <h2>Vad bestämmer livslängden?</h2>
        <p>
          Tre faktorer styr hur länge en fönsterfilm håller utan att börja gulna, blåsa
          eller lossna:
        </p>
        <CheckList
          items={[
            "Filmens egen kvalitet — 3M Prestige använder spektralt selektiva metaller som inte oxiderar.",
            "Var den sitter — invändig film håller längre eftersom den är skyddad från väder.",
            "Hur den rengörs — fel medel kan halvera livslängden på ett år.",
          ]}
        />
        <p>
          Vid normal skötsel håller invändig 3M-film 12–15+ år utan synlig nedbrytning.
          Utvändiga filmer (för fall där invändigt inte fungerar tekniskt) håller typiskt
          7–10 år.
        </p>

        <GuideInlineCta />
        <h2>Rengöring — så gör du rätt</h2>
        <StepProcess
          title="Korrekt rengöring av fönsterfilm"
          steps={[
            { title: "Damma av smuts torrt", body: "Använd en mjuk borste eller torr mikrofiberduk för att ta bort partiklar — annars sliper de ner i topcoaten när du går på vått." },
            { title: "Blanda mild lösning", body: "Ljummet vatten med en droppe milt diskmedel (utan citron eller slipmedel). Aldrig fönsterputs som innehåller ammoniak." },
            { title: "Tvätta med mikrofiber", body: "Använd ren mikrofiberduk eller mjuk svamp. Inga stålullsblock, skurborstar eller magic erasers." },
            { title: "Torka i en riktning", body: "Avsluta med torr mikrofiberduk i samma riktning — undviker strimmor och minimerar mekaniskt slitage." },
          ]}
        />

        <ProsCons
          prosTitle="Du kan använda"
          consTitle="Aldrig använd"
          pros={[
            "Ljummet vatten",
            "Milt diskmedel utan citron",
            "Ren mikrofiberduk",
            "Mjuk gummiskrapa (våt)",
            "Vanlig fönsterputs märkt ammoniakfri",
          ]}
          cons={[
            "Ammoniak (Ajax, Windex, klassisk fönsterputs)",
            "Slipmedel, skurpulver, magic eraser",
            "Stålull eller skurborste",
            "Vass skrapa eller rakblad",
            "Högtryckstvätt på utvändig film",
          ]}
        />

        <h2>Vad första månaden ser ut</h2>
        <p>
          Direkt efter installation kan filmen ha små vattenbubblor och se mjölkig ut.
          Detta är normalt — det är lim-vatten som torkar ut genom filmen och försvinner
          inom 2–4 veckor. Försök inte trycka ut bubblorna manuellt.
        </p>
        <Callout variant="warning" title="Vänta 30 dagar med första våtrengöringen">
          <p>
            Filmen härdar mot glaset under 2–4 veckor. Att tvätta för tidigt kan ge
            permanenta bubblor i kanten och påverkar garantin. Torrtorka räcker första
            månaden — sedan kör du standardrutinen ovan.
          </p>
        </Callout>

        <GuideInlineCta />
        <h2>Garantin i klartext</h2>
        <p>
          BBN Nordic lämnar:
        </p>
        <CheckList
          items={[
            "10 års produktgaranti från 3M på själva filmen.",
            "10 års arbetsgaranti på installationen.",
            "Dokumentation av installation och filmtyp för framtida ägare.",
            "Reklamationshantering direkt mot 3M utan mellanhand.",
          ]}
        />
        <p>
          Garantin gäller alla 3M-filmer monterade av BBN sedan 2008. Behöver du leta upp
          dokumentation för en gammal installation — ring 0771 502 502 så hjälper vi dig.
        </p>
      </GuideBody>
      <GuideFaq items={meta.faqs ?? []} />
      <GuideCta />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
